import React, { Dispatch, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExecutorGroup } from "../../../../services";
import { useTypedSelector } from "../../../../redux/store";
import { useExecutorGroupSubHandler } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";

const MultiSelectExecutorGroupWithModalBase: React.FC<
	MultiSelectExecutorGroupWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	sort,
	...props
}) => {
	const { t } = useTranslation();
	const lang = useTypedSelector((state) => state.session.language);

	const { getAllExecutorGroupSubHandler } = useExecutorGroupSubHandler();

	const items = useMemo(() => {
		const options: ExecutorGroup.SubscribeOptions = {};

		return getAllExecutorGroupSubHandler(options);
	}, [getAllExecutorGroupSubHandler]);

	const [error, setError] = useState("");

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			items.map((item) => ({
				key: item.id,
				label: item.name?.[lang],
				value: item.id,
				name: item.name?.[lang],
				data: item,
			})),
		[items, lang],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue) => {
			setError("");
			const ids = newValue.map((item) => item.value);
			onChange(ids);
		},
		[onChange],
	);

	const texts = useMemo(
		() => ({
			title: t(titleText),
			allText:
				t("common.modal.multiSelectExecutorGroupWithModal.str200") ??
				"",
			allActiveText:
				t("common.modal.multiSelectExecutorGroupWithModal.str200") ??
				"",
		}),
		[t, titleText],
	);

	return (
		<MultiSelectWithModal
			error={error}
			disabled={disabled}
			value={selected}
			title={texts.title}
			allText={texts.allText}
			allActiveText={texts.allActiveText}
			options={selectOptions}
			onChange={selectOnChange}
			required={required}
			pullUpItemInsideArray
			hiddenButton={false}
			showSelectAll
			modalSetting={modalSetting}
			sort={{
				show: true,
				active: true,
				...(sort || {}),
			}}
			{...props}
		/>
	);
};

export const MultiSelectExecutorGroupWithModal = memo(
	MultiSelectExecutorGroupWithModalBase,
);

declare namespace MultiSelectExecutorGroupWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		setShowModalProps?: (value: boolean) => void;
	}

	type Value = number[];
}

export default MultiSelectExecutorGroupWithModalBase;
